import { AssetSubClassCode } from '@api/api/entity-constants';
import { Entity } from '@api/api/entity-schema';
import { RangeHorizon, ResampleIntervals } from '@chart';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { webOnlyFeat } from '@terminal/config/app.config';
import { About } from '@terminal/modules/about/About';
import { LegacyEntityHeader } from '@terminal/modules/entity-header/LegacyEntityHeader';
import { OverviewEventFeed } from '@terminal/modules/overview-event-feed/OverviewEventFeed';
import { ThemeKnowledgeGraph } from '@terminal/modules/overview-knowledge-graph/components/theme-knowledge-graph/ThemeKnowledgeGraph';
import { ChartPrice } from '@terminal/modules/simple-chart/components/chart-price/ChartPrice';
import { SimpleChart } from '@terminal/modules/simple-chart/SimpleChart';
import { useEarningsInsightsByEntities } from '@terminal/react-query-hooks/earnings/useEarningsInsightsByEntities/useEarningsInsightsByEntities';
import { useCompanyTheme } from '@terminal/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { useSnakeMeta } from '@terminal/react-query-hooks/snakes/useSnake';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { useSubscription } from '@terminal/stores/use-subscription/useSubscription';
import { ChartSettings } from '@terminal/views/turbo-chart/utils/chart-settings-utils/chart-settings-utils';
import { useAssetOverviewNavigation } from '@terminal/widgets/asset-overview-widget/hooks/use-asset-overview-navigation/useAssetOverviewNavigation';
import { BrandsAndProductsSection } from '@terminal/widgets/knowledge-graph-widget/components/brands-and-products-section/BrandsAndProductsSection';
import { CompetitorsSection } from '@terminal/widgets/knowledge-graph-widget/components/competitors-tab/tabs/competitor-detail/components/competitors-section/CompetitorsSection';
import { EarningsInsightSection } from '@terminal/widgets/knowledge-graph-widget/components/earnings-insight-section/EarningsInsightSection';
import { GeographicExposureSection } from '@terminal/widgets/knowledge-graph-widget/components/geographic-exposure-tab/geographic-exposure-section/geographic-exposure-section/GeographicExposureSection';
import { UpgradeToUnlock } from '@terminal/widgets/overview/components/upgrade-to-unlock/UpgradeToUnlock';
import styles from './AssetOverviewContent.module.scss';

const ERROR_404_STATUS = 404;

export const AssetOverviewContent = ({
  entity,
  source,
}: {
  entity: Entity;
  source: PosthogTrackingSource;
}) => {
  const { data: snakeMeta } = useSnakeMeta(entity?.default_snake);
  const { navigateToChart } = useAssetOverviewNavigation(entity, source);

  const isBasicUser = useSubscription(state => state.isBasicUser());

  const isStock = entity.sub_class === AssetSubClassCode.EquityStock;
  const { error: companyThemeError } = useCompanyTheme({
    entityTag: entity.tag,
  });

  const { byEntityTag, isLoading: isEarningsLoading } =
    useEarningsInsightsByEntities([entity.tag]);
  const {
    latestEarningsData,
    earningsInsight,
    isPreview = false,
  } = byEntityTag.get(entity.tag) ?? {};

  const chartSettings: ChartSettings = {
    assets: [
      {
        tag: entity.tag,
        snake: entity.default_snake,
        paneIndex: 0,
      },
    ],
    resample: ResampleIntervals.OneDay,
    horizon: RangeHorizon.ThreeMonths,
    domain: null,
    hidden: undefined,
    paneSettings: {},
  };

  const displayConnections =
    companyThemeError?.status !== ERROR_404_STATUS && isStock;

  return (
    <>
      <LegacyEntityHeader
        className={styles.styledEntityHeader}
        entity={entity}
        truncateLongTitle
        truncateAtLength={40}
        isDrawer
        headerButton={<></>}
      />
      <div className={styles.contentContainer}>
        <div className={styles.chartWrapper}>
          <ChartPrice entity={entity} snakeMeta={snakeMeta} />
          <SimpleChart
            entity={entity}
            initialChartSettings={chartSettings}
            isLegacyVariant={false}
            onClickFullscreen={webOnlyFeat(navigateToChart)}
            maxHorizon={RangeHorizon.ThreeYears}
            eventsConfig={{
              TMI: true,
            }}
            source={source}
          />
        </div>
        <EarningsInsightSection
          entity={entity}
          earningsData={latestEarningsData}
          earningsInsight={earningsInsight}
          isLoading={isEarningsLoading}
          isPreview={isPreview}
        />
        {isBasicUser ? (
          <UpgradeToUnlock />
        ) : (
          <ErrorBoundaryWrapper>
            <OverviewEventFeed
              entityTag={entity.tag}
              isDrawer
              source={source}
            />
          </ErrorBoundaryWrapper>
        )}
        <About
          entity={entity}
          variant="secondary"
          showMoreDescription
          limit={300}
        />
        {displayConnections && (
          <>
            <ThemeKnowledgeGraph
              entityTag={entity.tag}
              source={source}
              isInDrawer
            />
            <CompetitorsSection
              entityTag={entity.tag}
              source={source}
              isInDrawer
            />
            <GeographicExposureSection
              entityTag={entity.tag}
              source={source}
              isInDrawer
            />
            <BrandsAndProductsSection
              entityTag={entity.tag}
              source={source}
              isInDrawer
            />
          </>
        )}
      </div>
    </>
  );
};
